import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { MyButton, MyModalSlider, myToaster } from '@interstellar-component'

import { useApp } from '@src/AppContext'
import { CompanyProvider, useCompany } from './Context'
import { CompanyService, isTransportFailure, loadFailureMessage } from './service'
import { memberStatusOf } from './components/MyMemberStatusChip'
import {
  agreementStatusOf,
  signingEnvelopeStatus,
  withAgreementMessage,
} from './components/agreementStatus'
import { enrolmentPresentation } from './components/CompanyInformation/statusPresentation'
import RejectionPanel from './components/MyDetailSlider/RejectionPanel'
import CompanyInformation from './components/CompanyInformation'
import EmptyState from './components/CompanyInformation/EmptyState'
import ResendApplicationPanel from './components/MyDetailSlider/ResendApplicationPanel'
import SubscribePanel from './components/MyDetailSlider/SubscribePanel'
import TopupPanel from './components/MyDetailSlider/PurchaseQuotaModal'
import InvoicePaymentPanel from './components/MyDetailSlider/InvoicePaymentPanel'

// Must match RegisterCompanyInfo/Context COMPANY_ID_STORAGE_KEY — the enrollment
// wizard resumes the draft whose id is in this localStorage key on mount.
const REGISTER_COMPANY_ID_KEY = 'register_company_id'

// Same pills statusPresentation.js paints its enrolment badges with. Duplicated
// rather than exported because these two are for states that module has no
// opinion about — a request that has not landed, and one that failed.
const NEUTRAL_PILL = 'border-gray/300 bg-gray/50 text-gray/700'
const ERROR_PILL = 'border-error/200 bg-error/50 text-error/700'

/**
 * Has the company finished the enrolment wizard?
 *
 * NOT `enrollment_status === 'completed'`. That column has no CHECK constraint
 * and carries a fifth value in production — 'active' — on companies that are
 * fully onboarded and CLIK-approved. Reading it as an equality dropped those
 * into the "still enrolling" branch, which showed them a green "Approved" chip
 * directly above "Pendaftaran perusahaan belum selesai", and whose only button
 * bounced them into the wizard, which bounced them straight back here.
 *
 * `verification_status` is the stronger signal: it is only ever set once the
 * application has been submitted for review, so its presence alone proves the
 * wizard is behind the company.
 */
function enrolmentFinished(company) {
  if (company?.verification_status) return true
  return ['completed', 'active'].includes(company?.enrollment_status)
}

function SingleCompanyInner({ seed }) {
  const navigate = useNavigate()
  // getSession is the retry for the seed itself: `seed` comes from
  // company_context on /auth/session, so re-running that call is what can make
  // a missing company appear — nothing on this page can.
  const { getSession } = useApp()
  const {
    companyDetail,
    fetchCompanyDetail,
    openResendPanel,
    resendPanelOpen,
    closeResendPanel,
    subscribePanelOpen,
    closeSubscribePanel,
    topupPanelOpen,
    closeTopupPanel,
    invoicePanelId,
    closeInvoicePanel,
    closeBillingPanels,
  } = useCompany()

  const companyId = seed?.id
  // Branch off the (stable) session seed, not the async detail:
  //  - active            → full billing page (package + saldo + statement)
  //  - enrolment done    → verification phase → same page, empty state only
  //  - still enrolling   → same page, empty state naming the outstanding step
  const seedStatus = memberStatusOf(seed)
  const isActive = seedStatus === 'active'
  const onboardingComplete = enrolmentFinished(seed)
  const enrollmentStep = Number(seed?.enrollment_step) || 1

  // Step 4 means every form is submitted and only the membership agreement is
  // left, so the page has to ask DocuSign what happened to it. Below step 4
  // there is nothing to ask about — the agreement has not been generated yet.
  const [signing, setSigning] = useState(null)
  const [resending, setResending] = useState(false)
  const needsSigningStatus = !onboardingComplete && enrollmentStep >= 4

  // Three distinct things this fetch can be in the middle of, and the page says
  // a different sentence for each:
  //   'loading' → we do not know yet          (say so; claim no step)
  //   'ready'   → the service answered        (enrolmentPresentation decides)
  //   'error'   → the service never answered  (say so; offer a retry)
  // Collapsing 'loading' and 'error' into 'ready' is what made a down backend
  // read as a settled fact — see the catch below.
  const [signingState, setSigningState] = useState(() =>
    needsSigningStatus ? 'loading' : 'idle'
  )
  const [signingError, setSigningError] = useState(null)

  const loadSigning = useCallback(async () => {
    if (!companyId || !needsSigningStatus) {
      setSigningState('idle')
      return
    }
    setSigningState('loading')
    setSigningError(null)
    try {
      const res = await CompanyService.signingStatus(companyId)
      setSigning(res?.data ?? res ?? null)
      setSigningState('ready')
    } catch (err) {
      // A 404 is an ANSWER: findActiveEnvelope() excludes voided/declined, so
      // "never sent" and "declined and dropped" both come back as one, and both
      // genuinely mean "not out for signature".
      //
      // A transport failure is NOT an answer. Swallowing it the same way told a
      // company parked at step 4 that it still had to send an agreement that may
      // already have been sitting in the signer's inbox for days — and offered
      // no hint that the reason was simply that the backend was unreachable.
      setSigning(null)
      setSigningState(isTransportFailure(err) ? 'error' : 'ready')
      setSigningError(isTransportFailure(err) ? loadFailureMessage(err) : null)
    }
  }, [companyId, needsSigningStatus])

  useEffect(() => {
    loadSigning()
  }, [loadSigning])

  useEffect(() => {
    if (!companyId) return
    if (isActive) {
      // Full company (verification_status + logo) + billing bundle for header.
      fetchCompanyDetail(companyId, { type: 'billing' })
    } else if (onboardingComplete) {
      fetchCompanyDetail(companyId, { type: 'information' }) // header logo/name
      fetchCompanyDetail(companyId, { type: 'result' }) // verification verdict + note
    } else {
      fetchCompanyDetail(companyId, { type: 'information' }) // header logo/name
    }
  }, [companyId, isActive, onboardingComplete, fetchCompanyDetail])

  const detail =
    companyDetail && String(companyDetail.id) === String(companyId)
      ? companyDetail
      : null
  const company = detail || seed

  const handleResume = () => {
    if (companyId) localStorage.setItem(REGISTER_COMPANY_ID_KEY, companyId)
    navigate('/register-company-info')
  }

  const handleResend = async () => {
    if (!companyId || resending) return
    setResending(true)
    try {
      const res = await CompanyService.resendAgreement(companyId)
      myToaster(res)
      await loadSigning()
    } catch (err) {
      // NetworkUtils rejects with the API body itself. 422
      // already_signed_by_client: the signature landed meanwhile — re-read the
      // status so the page stops offering a resend.
      myToaster(
        err?.message
          ? withAgreementMessage(err)
          : { status: 400, message: 'Failed to resend the agreement' }
      )
      if (err?.code === 'already_signed_by_client') {
        await loadSigning()
        getSession()
      }
    } finally {
      setResending(false)
    }
  }

  // The un-finished-enrolment presentation: which pill, which copy, which
  // buttons. Everything it says is derived from enrollment_step and the
  // envelope, so the page never claims a step the company has already passed.
  let presentation = null
  if (!onboardingComplete && signingState === 'loading') {
    // Before the answer lands there is no honest step to name. The old code
    // rendered enrolmentPresentation against `signing === null`, i.e. it
    // asserted "tinggal tanda tangan" on every first paint and then flipped to
    // "menunggu tanda tangan" once the envelope arrived.
    presentation = {
      badge: { label: 'Loading status', cls: NEUTRAL_PILL },
      title: 'Loading registration status…',
      description: 'Fetching the membership agreement status from the server.',
    }
  } else if (!onboardingComplete && signingState === 'error') {
    presentation = {
      badge: { label: 'Status unknown', cls: ERROR_PILL },
      title: 'Failed to load registration status',
      description: `${signingError} Until the status can be read, this page cannot tell which registration step is still left.`,
      // Marks this as a failure rather than an emptiness, so EmptyState paints
      // it as an error and announces it. Without it the copy lands in the same
      // grey card as "nothing here yet".
      variant: 'error',
      actions: (
        <>
          <MyButton
            color="primary"
            variant="filled"
            size="md"
            type="button"
            onClick={loadSigning}
          >
            <span className="text-sm-semibold text-white">Try again</span>
          </MyButton>
          {/* Kept alongside the retry: handleResume only writes localStorage and
              navigates, so it still works while the backend is unreachable.
              Dropping it left a company parked at step 4 with no way forward at
              all until the retry happened to succeed. */}
          <MyButton
            color="secondary"
            variant="outlined"
            size="md"
            type="button"
            onClick={handleResume}
          >
            <span className="text-sm-semibold">Continue registration</span>
          </MyButton>
        </>
      ),
    }
  } else if (!onboardingComplete) {
    const base = enrolmentPresentation({
      step: enrollmentStep,
      // A declined / voided envelope is not out for signature, whatever
      // signing-status says about it.
      envelope: signingEnvelopeStatus(signing),
      signerEmail: signing?.signer_email || null,
      // signing-status 404s once the envelope is declined / voided (e.g. after
      // an agreement reset); the company detail still names it as the latest
      // agreement until a new one is sent → "Please sign the agreement again".
      agreement: signing?.agreement_status || agreementStatusOf(detail),
    })
    presentation = {
      ...base,
      actions: (
        <>
          {base.canContinue !== false ? (
            <MyButton
              color="primary"
              variant="filled"
              size="md"
              type="button"
              onClick={handleResume}
            >
              <span className="text-sm-semibold text-white">
                {base.continueLabel || 'Continue registration'}
              </span>
            </MyButton>
          ) : null}
          {base.canResend ? (
            <MyButton
              color="secondary"
              variant="outlined"
              size="md"
              type="button"
              disabled={resending}
              onClick={handleResend}
            >
              <span className="text-sm-semibold">
                {resending ? 'Sending…' : 'Resend email'}
              </span>
            </MyButton>
          ) : null}
        </>
      ),
    }
  }

  // No seed means /auth/session did not hand us a company — which happens when
  // that call FAILED, not only when the holding is genuinely empty. The page
  // must not fill the gap with a company-shaped shell built from `undefined`
  // (blank name, "—" everywhere, buttons wired to `companyId === undefined`);
  // that reads as a broken company rather than as a failed read. Say what
  // happened and offer the one action that can fix it.
  if (!companyId) {
    return (
      <div className="flex h-full w-full flex-col bg-white">
        <div className="mx-auto flex w-full max-w-[1180px] flex-col px-6 py-8">
          <EmptyState
            variant="error"
            title="Failed to load company data"
            description="We could not read the company in your session. This does not mean you have no company — try reloading."
            onRetry={getSession}
            retryLabel="Reload"
          />
        </div>
      </div>
    )
  }

  const anyPanelOpen =
    subscribePanelOpen || topupPanelOpen || !!invoicePanelId || resendPanelOpen

  // Slide-overs opened from CompanyInformation's / the rejection panel's actions
  // via the CompanyProvider state.
  const panels = (
    <MyModalSlider
      scrim
      open={anyPanelOpen}
      element={
        <div className="flex h-full flex-row-reverse">
          {subscribePanelOpen ? (
            <SubscribePanel companyId={companyId} onClose={closeSubscribePanel} />
          ) : null}
          {topupPanelOpen ? (
            <TopupPanel companyId={companyId} onClose={closeTopupPanel} />
          ) : null}
          {invoicePanelId ? (
            <InvoicePaymentPanel
              key={invoicePanelId}
              companyId={companyId}
              invoiceId={invoicePanelId}
              onClose={closeInvoicePanel}
            />
          ) : null}
          {resendPanelOpen ? (
            <ResendApplicationPanel companyId={companyId} onClose={closeResendPanel} />
          ) : null}
        </div>
      }
      onClose={closeBillingPanels}
    />
  )

  // ONE page for every state. The design's three landing frames (15172:24049
  // active, 15564:30150 awaiting CLIK, 15580:27719 UMKM-rejected) share an
  // identical shell — Back, the "Company information" heading, the Company
  // profile link, the centred identity block — and swap only the body. The
  // narrow amber-banner shell that used to serve mid-enrolment companies
  // appears in no frame at all; it threw that shell away and, worse, told a
  // company parked at step 4 to "lengkapi dokumen" it had already uploaded.
  //
  // NOTHING follows the empty-state card except a rejection notice. 15564:30150
  // ends the page there, and the two blocks that used to sit underneath belong
  // elsewhere: the CLIK credential card is owned by the Company profile
  // sub-page (15182:157192, and CompanyProfile already renders it), and the
  // activity timeline has no counterpart in any frame. The rejection notice
  // stays because it is the only route out of a rejected application — the
  // empty state names the verdict, this panel carries the reviewer's reason and
  // the resubmit button.
  return (
    <div className="flex h-full w-full flex-col bg-white">
      <CompanyInformation
        companyId={companyId}
        company={company}
        presentation={presentation}
        footer={
          memberStatusOf(company) === 'rejected' ? (
            <RejectionPanel
              reason={company?.verification?.note}
              reviewedAt={company?.verification?.reviewed_at}
              onRevise={openResendPanel}
            />
          ) : null
        }
      />
      {panels}
    </div>
  )
}

SingleCompanyInner.propTypes = {
  seed: PropTypes.object,
}
SingleCompanyInner.defaultProps = { seed: null }

/**
 * Single-company view — rendered when the user's holding has exactly one
 * company. Wrapped in CompanyProvider so it can reuse the detail-fetch + panel
 * machinery (rejection/revise + billing) developed for the detail slider.
 */
function SingleCompany({ company }) {
  return (
    <CompanyProvider>
      <SingleCompanyInner seed={company} />
    </CompanyProvider>
  )
}

SingleCompany.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    enrollment_status: PropTypes.string,
    enrollment_step: PropTypes.number,
    verification_status: PropTypes.string,
    agreement_expires_at: PropTypes.string,
  }),
}

SingleCompany.defaultProps = {
  company: null,
}

export default SingleCompany
